import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Shell } from "./Shell";
import { FadeUp } from "./Sections";

const HERO_IMG =
  "https://harcresearch.org/wp-content/uploads/2020/08/research-science-impact-support-nature-industry-humans.jpg";

const STEPS = [
  {
    name: "Understand",
    color: "#004778",
    text: "We use independent research, data, and technical expertise to understand the challenge, uncover insights, and identify opportunities.",
  },
  {
    name: "Connect",
    color: "#83BDE4",
    text: "We engage stakeholders, partners, subject-matter experts, and resources to create a more complete understanding of the challenge and what's possible.",
  },
  {
    name: "Solve",
    color: "#7EBC37",
    text: "We translate science and shared knowledge into strategies and solutions designed around real-world needs.",
  },
  {
    name: "Impact",
    color: "#FFA100",
    text: "We help create a path from strategy to implementation, turning good ideas into measurable outcomes and lasting impact.",
  },
];

const SECTORS = ["Government", "Business", "Nonprofits", "Philanthropy", "Communities", "Academia"];

export default function Approach() {
  return (
    <Shell>
      <div data-testid="approach-page">
        <section className="relative flex min-h-[60vh] items-end">
          <img
            src={HERO_IMG}
            alt="Research, science, and impact supporting nature, industry, and people"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            className="absolute inset-0 bg-gradient-to-r from-[#022A42]/85 via-[#022A42]/40 to-transparent"
            aria-hidden="true"
          />
          <div className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-32">
            <FadeUp>
              <p className="mb-5 text-xs font-bold uppercase tracking-[0.18em] text-white/80">
                Our Approach
              </p>
              <h1 className="harc-heading max-w-3xl text-4xl font-light leading-[1.08] tracking-tight text-white md:text-6xl">
                Complex Challenges Require a Different Approach
              </h1>
            </FadeUp>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="approach-interconnected">
          <FadeUp className="max-w-3xl">
            <p className="mb-5 text-xs font-bold uppercase tracking-[0.18em] text-[#004778]">
              Understanding Interconnectedness
            </p>
            <p className="text-base leading-relaxed text-[#5C6B72] md:text-lg md:font-light">
              Today's environmental, infrastructure, and community challenges are increasingly
              interconnected. Energy affects infrastructure. Water affects growth. Air quality
              affects health. And environmental decisions can have lasting impacts on economies and
              communities. Solving these challenges requires more than identifying the problem. It
              takes credible science, diverse expertise, strong partnerships, and a clear path to
              action. HARC brings these elements together, turning insight into practical solutions
              that create lasting impact.
            </p>
            <Link to="/impact" className="mockup-link-arrow mt-8" data-testid="approach-impact-cta">
              Explore Our Impact <ArrowRight size={15} aria-hidden="true" />
            </Link>
          </FadeUp>
        </section>

        <section className="bg-[#EEF3F5] py-24 md:py-32" data-testid="approach-steps">
          <div className="mx-auto max-w-7xl px-6">
            <FadeUp>
              <p className="mb-5 text-xs font-bold uppercase tracking-[0.18em] text-[#004778]">
                How HARC Creates Solutions
              </p>
              <p className="max-w-2xl text-base leading-relaxed text-[#5C6B72]">
                Our approach connects insight, people, and action, helping move ideas beyond
                research and into real-world solutions.
              </p>
            </FadeUp>
            <div className="mt-12 grid gap-5 md:grid-cols-4">
              {STEPS.map((s, i) => (
                <FadeUp key={s.name} delay={i * 0.08}>
                  <div
                    className="h-full border-t-4 bg-white p-7"
                    style={{ borderTopColor: s.color }}
                    data-testid={`approach-step-${s.name.toLowerCase()}`}
                  >
                    <p className="harc-heading text-sm font-bold uppercase tracking-[0.16em] text-[#004778]">
                      {String(i + 1).padStart(2, "0")} · {s.name}
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-[#5C6B72]">{s.text}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.2}>
              <Link
                to="/start-a-conversation"
                className="mockup-link-arrow mt-10"
                data-testid="approach-talk-cta"
              >
                Talk to HARC <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </FadeUp>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="approach-complex">
          <FadeUp className="max-w-3xl">
            <p className="mb-5 text-xs font-bold uppercase tracking-[0.18em] text-[#004778]">
              Built for Complex Challenges
            </p>
            <p className="text-base leading-relaxed text-[#5C6B72] md:text-lg md:font-light">
              The most difficult challenges rarely fit neatly within a single discipline. Energy
              decisions can affect infrastructure and economic growth. Water impacts ecosystems and
              communities. Air quality influences both environmental and human health. HARC works
              across disciplines to see the connections others may miss. Whether you're facing a
              defined challenge or still trying to understand the path forward, HARC can help bring
              the right science, people, and solutions together.
            </p>
            <Link
              to="/start-a-conversation"
              className="mockup-link-arrow mt-8"
              data-testid="approach-conversation-cta"
            >
              Start a Conversation With HARC <ArrowRight size={15} aria-hidden="true" />
            </Link>
          </FadeUp>
        </section>

        <section className="bg-[#004778] py-24 md:py-32" data-testid="approach-partners">
          <div className="mx-auto max-w-7xl px-6">
            <FadeUp>
              <h2 className="harc-heading max-w-2xl text-3xl font-light leading-tight text-white md:text-5xl">
                Better Solutions Start With the Right Partners
              </h2>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80">
                Complex challenges rarely belong to one organization or one sector. HARC works
                across sectors to bring together the expertise, perspectives, and resources needed
                to develop solutions that work in the real world.
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="mt-10 flex flex-wrap gap-3" data-testid="approach-sectors">
                {SECTORS.map((s) => (
                  <Link
                    key={s}
                    to="/impact-story"
                    className="rounded-full border border-white/40 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-[#C9DA2D] hover:text-[#C9DA2D]"
                    data-testid={`sector-${s.toLowerCase()}`}
                  >
                    {s}
                  </Link>
                ))}
              </div>
              <p className="mt-8 max-w-2xl text-sm leading-relaxed text-white/70">
                Whether working with a single organization or bringing multiple stakeholders
                together around a shared challenge, HARC helps connect the right people and
                knowledge to move from insight to action.
              </p>
              <Link
                to="/start-a-conversation"
                data-testid="approach-solve-cta"
                className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#FFA100] px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#022A42] transition-colors hover:bg-[#D98A00]"
              >
                Let's Solve Something Together <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </FadeUp>
          </div>
        </section>
      </div>
    </Shell>
  );
}
